import React from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { track } from '@amplitude/analytics-browser';
import { useSelector } from 'react-redux';
import { getIsInternalUser } from '../../../../features/video-configuration/videoConfigurationSlice';
import { getUserProfile } from '../../../../features/user/userSlice';
import { GROUP_NAME_EXTERNAL } from '../../../constants/configuration';
import { selectAuthInfo } from '../../../../features/auth/authSlice';

// Assets
import { ReactComponent as AppStore } from '../../../assets/images/svg/appButton.svg';
import { ReactComponent as AppGoogle } from '../../../assets/images/svg/GoogleButton.svg';
import { ReactComponent as AndroidTV } from '../../../assets/images/svg/droidTvbutton.svg';
import { ReactComponent as AppleTv } from '../../../assets/images/svg/appTvbutton.svg';
import { ReactComponent as Tizen } from '../../../assets/images/svg/samsung.svg';
import { ReactComponent as WebOs } from '../../../assets/images/svg/lg.svg';
import { ReactComponent as InstagramFooter } from '../../../assets/images/svg/instagramFooter.svg';
import { ReactComponent as FacebookFooter } from '../../../assets/images/svg/facebookFooter.svg';
import { ReactComponent as ThreadsFooter } from '../../../assets/images/svg/threads.svg';
import { ReactComponent as TiktokFooter } from '../../../assets/images/svg/tiktok.svg';

const FooterNav = () => {
  const { t, i18n } = useTranslation();
  const lang = i18n.language;
  const isInternalUser = useSelector(getIsInternalUser);
  const user = useSelector(getUserProfile);
  const { isAuthorized } = useSelector(selectAuthInfo);
  const isExternalUser =
    (!isAuthorized && !isInternalUser) || user.groups?.[0]?.name === GROUP_NAME_EXTERNAL;

  const analyticsEvent = (app) => {
    track(' Market Clicked', { MarketTitle: app });
  };
  return (
    <div className="FooterNav">
      <div className="Container">
        <nav>
          <div className="FooterNav__wrap">
            <section>
              <p className="Footer__heading">{t('footer.titles.forUsers')}</p>
              <div className="Footer__links-wrap">
                <div>
                  <ul>
                    {t('footer.navList1', { returnObjects: true }).map(({ title, path }) => (
                      <li key={title}>
                        <Link to={path}>{title}</Link>
                      </li>
                    ))}
                  </ul>
                </div>
                <div>
                  <ul>
                    {t('footer.navList2', { returnObjects: true }).map(({ title, path }) => (
                      <li key={title}>
                        <Link to={path}>{title}</Link>
                      </li>
                    ))}
                  </ul>
                </div>
                <div>
                  <ul>
                    {t('footer.navList3', { returnObjects: true }).map(({ title, path }) => (
                      <li key={title}>
                        <Link to={path}>{title}</Link>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </section>
            <section>
              <div className="Footer__links-wrap">
                <div>
                  <ul>
                    {t('footer.navList3', { returnObjects: true }).map(({ title, path }) => (
                      <li key={title}>
                        <Link to={path}>{title}</Link>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </section>
            <section>
              <p className="Footer__heading">{t('footer.titles.phones')}</p>
              <div className="Footer__links-wrap">
                <div>
                  <ul>
                    {t(
                      isExternalUser
                        ? 'footer.contactsExternalColumn1'
                        : 'footer.contactsInternalColumn1',
                      {
                        returnObjects: true
                      }
                    )?.map(({ title, href }) => (
                      <li key={title}>
                        <a href={href}>{title}</a>
                      </li>
                    ))}
                  </ul>
                </div>
                <div>
                  <ul>
                    {t(
                      isExternalUser
                        ? 'footer.contactsExternalColumn2'
                        : 'footer.contactsInternalColumn2',
                      {
                        returnObjects: true
                      }
                    )?.map(({ title, href }) => (
                      <li key={title}>
                        <a href={href}>{title}</a>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
              <p className="Footer__heading social">{t('footer.titles.socials')}</p>
              <div className="Footer__links-social">
                <div>
                  <ul>
                    <li>
                      <a
                        href="https://www.facebook.com/briz.tv.official"
                        target="_blank"
                        rel="noreferrer"
                        aria-label="Facebook"
                      >
                        <FacebookFooter />
                      </a>
                    </li>
                    <li>
                      <a href="https://www.instagram.com/briz_tv/" target="_blank" rel="noreferrer" aria-label="instagram">
                        <InstagramFooter />
                      </a>
                    </li>
                    <li>
                      <a
                        href="https://www.threads.com/@briz_tv?invite=0"
                        target="_blank"
                        rel="noreferrer"
                        aria-label="Threads"
                      >
                        <ThreadsFooter />
                      </a>
                    </li>
                    <li>
                      <a
                        href="https://www.tiktok.com/@_briz.tv_"
                        target="_blank"
                        rel="noreferrer"
                        aria-label="Tiktok"
                      >
                        <TiktokFooter />
                      </a>
                    </li>
                  </ul>
                </div>
              </div>
            </section>
          </div>

          <section className="FooterNav__apps">
            <p className="Footer__heading">{t('footer.titles.apps')}</p>
            <ul>
              <li>
                <a
                  target="_blank"
                  onClick={() => analyticsEvent('apple')}
                  href="https://apps.apple.com/ua/app/briz-tv/id1539403361?!=ru"
                  rel="noreferrer"
                  aria-label="AppStore"
                >
                  <AppStore />
                </a>
              </li>
              <li>
                <a
                  target="_blank"
                  onClick={() => analyticsEvent('apple')}
                  href="https://apps.apple.com/ua/app/briz-tv/id1539403361?!=ru"
                  rel="noreferrer"
                  aria-label="AppleTv"
                >
                  <AppleTv />
                </a>
              </li>
              <li>
                <a
                  target="_blank"
                  rel="noreferrer"
                  onClick={() => analyticsEvent('google')}
                  href={`https://play.google.com/store/apps/details?id=ua.briz.androidtv&hl=${
                    lang == 'ru' ? 'ru' : 'uk'
                  }`}
                  aria-label="AppGoogle"
                >
                  <AppGoogle />
                </a>
              </li>
              <li>
                <a
                  target="_blank"
                  rel="noreferrer"
                  onClick={() => analyticsEvent('google')}
                  href={`https://play.google.com/store/apps/details?id=ua.briz.androidtv&hl=${
                    lang == 'ru' ? 'ru' : 'uk'
                  }`}
                  aria-label="AndroidTV"
                >
                  <AndroidTV />
                </a>
              </li>
              <li>
                <a
                  target="_blank"
                  onClick={() => analyticsEvent('samsung')}
                  href={`https://www.briz.ua/${lang}/help/briz-tv-na-televizor-samsung`}
                  rel="noreferrer"
                  aria-label="Tizen"
                >
                  <Tizen />
                </a>
              </li>
              <li>
                <a
                  target="_blank"
                  onClick={() => analyticsEvent('lg')}
                  href="https://ua.lgappstv.com/main/tvapp/detail?appId=1130160"
                  rel="noreferrer"
                  aria-label="WebOs"
                >
                  <WebOs />
                </a>
              </li>
            </ul>
          </section>
        </nav>
      </div>
    </div>
  );
};

export default React.memo(FooterNav);
